import Typography from "@mui/material/Typography";
import React, {useState} from "react";
import classnames from "classnames";
import Popover from "@mui/material/Popover";
import DeleteSharpIcon from "@mui/icons-material/DeleteSharp";
import {DealModal} from "../PricingListPage/DealModal";
import {useFetchHook} from "../../hooks/useFetchHook";
import fetcher from "../../fetcher";
import useDidMountEffect from "../../hooks/useDidMountEffect";
import {toast} from "react-toastify";
import {useListDealsStore} from "../../stores/listDealsStore";
import {useModalsStore} from "../../stores/modalsStore";
import {useMediaQuery} from "react-responsive";
import {mediaQueryForMobile} from "../HomePage/HomePage";
import {useShallow} from "zustand/react/shallow";

export const ListDealCard = ( { deal, isSelected } ) => {
    const isTabletOrMobile = useMediaQuery({ query: mediaQueryForMobile })
    const openModal = useModalsStore((state) => state.openModal)
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [isDeleting, setIsDeleting] = useState(false)

    const [deleteDealResponse, deleteDeal] = useFetchHook(fetcher.deleteDeal)

    const {
        updateDealFromBackend,
        removeDeletedDealFromList,
    } = useListDealsStore(
        useShallow((state) => ({
            removeDeletedDealFromList: state.removeDeletedDealFromList,
            updateDealFromBackend: state.updateDealFromBackend,
        }))
    )


    useDidMountEffect(function updateListAfterDelete() {
        if (deleteDealResponse?.data?.data?.id) {
            removeDeletedDealFromList(deleteDealResponse?.data?.data?.id)
            toast.success("Successfully deleted deal.")
        }
    }, [deleteDealResponse])

    const car_detail = deal.car_detail
    const quote = deal.formatted_quote || {}

    const rawDealType = quote.deal_type || (deal.is_purchase ? 'cash' : 'lease')
    const dealType = String(rawDealType).toLowerCase()
    const isLease = dealType === 'lease'
    const isFinance = dealType === 'finance'
    const isCash = dealType === 'cash' || dealType === 'purchase'
    const normalizedTypeKey = isCash ? 'cash' : (isFinance ? 'finance' : 'lease')
    const dealTypeLabel = isCash ? 'CASH' : (isFinance ? 'FINANCE' : 'LEASE')

    const isActive = deal.active !== false
    const statusColor = isActive ? '#4ade80' : '#f87171'
    const statusText = isActive ? 'Active' : 'Expired'

    const handleClick = (event) => {
        event.stopPropagation()
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleDelete = (id) => {
        return deleteDeal(id)
    }

    const handleCardClick = () => {
        if (isTabletOrMobile) {
            window.open(`/quotes/${deal.id}`, "_self")
        } else {
            openModal({
                element: DealModal,
                id: "DealModal",
                props: {
                    id: deal.id,
                    onSave: (newDealState) => updateDealFromBackend(newDealState),
                },
            })
        }
    }

    const amountLeftLabel = isLease ? 'Due at Signing' : (isFinance ? 'Down Payment' : 'Sale Price')

    return (
        <div
            onClick={handleCardClick}
            className={classnames('ListDealCard', `type-${normalizedTypeKey}`, {
                selected: isSelected,
                isPurchase: isCash
            })}
        >
            <div className={'card-header'}>
                <div className={'card-title-row'}>
                    <div className={'title-left'}>
                        <div className={'card-title'}>
                            {car_detail.year} {car_detail.make} {car_detail.model}{car_detail.is_new ? "" : " [Used]"}
                        </div>
                        <div className={'card-spec'}>
                            {car_detail.trim}
                        </div>
                    </div>
                    <div className={'title-right'}>
                        <div className={'status-dot'}>
                            <span
                                className={'dot'}
                                style={{ background: statusColor }}
                            />
                            <span
                                className={'status-label'}
                                style={{ color: statusColor }}
                            >
                                {statusText}
                            </span>
                        </div>
                        <button
                            type="button"
                            className={classnames('menu-btn', { loader: isDeleting })}
                            onClick={handleClick}
                            aria-label="More"
                        >
                            ···
                        </button>
                    </div>
                </div>
                <div className={'card-subtitle'}>{quote.lender_name}</div>
                {deal.description ? (
                    <div className={'card-note'}>{deal.description}</div>
                ) : null}
            </div>

            <Popover
                onClick={(e) => e.stopPropagation()}
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    horizontal: 'right',
                    vertical: 'top'
                }}
            >
                <Typography
                    sx={{ p: 2, fontWeight: 500 }}
                    className={'ListDealCard-menu-item'}
                    onClick={() => handleDelete(deal.id)}
                >
                    Delete
                    <DeleteSharpIcon
                        className={classnames(
                            'action-icon', {
                                loader: isDeleting
                            }
                        )}
                    />
                </Typography>
            </Popover>

            <div className={classnames('card-body', normalizedTypeKey)}>
                <div className={'deal-type-row'}>
                    <span className={'deal-type'}>{dealTypeLabel}</span>
                    {quote.msrp ? (
                        <span className={'msrp-label'}>MSRP ${quote.msrp}</span>
                    ) : null}
                </div>
                <div className={'amounts-row'}>
                    <div className={'amount-left'}>
                        <div className={'big'}>${quote.amount_due_at_signing}</div>
                        <div className={'lbl'}>{amountLeftLabel}</div>
                    </div>
                    {!isCash && (
                        <div className={'amount-right'}>
                            <div className={'big'}>${quote.monthly_payment}</div>
                            <div className={'lbl'}>/ month</div>
                        </div>
                    )}
                </div>
                <div className={'stats-row'}>
                    {isCash ? (
                        <div className={'stat'}>
                            <div className={'val'}>${deal.profit}</div>
                            <div className={'key'}>PROFIT</div>
                        </div>
                    ) : (
                        <React.Fragment>
                            <div className={'stat'}>
                                <div className={'val'}>{deal.term}</div>
                                <div className={'key'}>MONTHS</div>
                            </div>
                            <div className={'stat'}>
                                <div className={'val'}>{deal.mileage}</div>
                                <div className={'key'}>MILES</div>
                            </div>
                            <div className={'stat'}>
                                <div className={'val'}>${deal.profit}</div>
                                <div className={'key'}>PROFIT</div>
                            </div>
                        </React.Fragment>
                    )}
                </div>
            </div>
        </div>
    )
}
